import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { HtmlInputType, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { ToastTypes } from '../../../../shared/services/abstract/toast.service.abstract';
import { PinResetService } from '../../../../shared/services/pin-reset.service';
import { PinResetComponent } from '../../pin-reset/pin-reset.component';
import { ToastService } from '../../../../shared/services/toast.service';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { PhoneUiContext } from '../../../../shared/phone-buttons/contexts/phone-ui.context';
import { HelpdeskOptionsContext } from '../../../shared/contexts/helpdesk-options.context';
import { Button, CiscoHelpdeskOptions, LineButton, Phone } from '../../../../shared/models/generated/ziroModels';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { SiteContext } from '../../../shared/contexts/site.context';
import { DnDetailSummaryResource } from '../../../../shared/resources/dn-detail-summary.resource';
import { switchMap } from 'rxjs/operators';
import { isEqual } from 'lodash';
import { SiteSummaryContext } from '../../../../shared/contexts/site-summary.context';

export interface PinReset {
  vmPinReset: string;
}

@Component({
  selector: 'ziro-public-phone-pin-reset',
  templateUrl: './public-phone-pin-reset.component.html',
  standalone: false,
})
export class PublicPhonePinResetComponent extends ZiroFormAbstractDirective<PinReset> implements OnInit, OnDestroy {
  protected _ziroFormStateService: ZiroFormStateService;
  private _route = inject(ActivatedRoute);
  private _pinResetService = inject(PinResetService);
  private _toastService = inject(ToastService);
  private _phoneUiContext = inject(PhoneUiContext);
  private _siteContext = inject(SiteContext);
  private _helpdeskOptionsContext = inject(HelpdeskOptionsContext);
  private _dnDetailSummaryResource = inject(DnDetailSummaryResource);
  private _siteSummaryContext = inject(SiteSummaryContext);

  @ViewChild('vmPin') vmPinResetComponent: PinResetComponent;

  isLoading = true;
  errorMessage = '';
  formConfig = {
    fields: {
      vmPinReset: {
        dataAutomation: 'public-phone-voicemail-pin-reset',
        label: 'tkey;pages.pinreset.voicemail',
        componentConfig: new ZiroTextConfig({
          htmlInputType: HtmlInputType.PASSWORD,
          placeholder: 'tkey;pages.reset.pin.placeholder',
        }),
        validation: [
          {
            validator: (val: string) => {
              return !val || (!!val && /(^[0-9]+$)/.test(val))
                ? ZiroFormsValidationState.VALID
                : ZiroFormsValidationState.INVALID;
            },
            message: 'tkey;validators.global.error.pattern',
          },
          {
            validator: () => (this._hasError ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID),
            message: () => this.errorMessage,
          },
        ],
      },
    },
  } as ZiroFormConfig;
  private _phone: Phone;
  private _ziroIcons = ZiroIcons;
  private _hasError = false;
  private _resetPinOnNextLogin = false;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    const subscription = combineLatest([
      this._phoneUiContext.phoneState$,
      this._helpdeskOptionsContext.state$,
      this._siteSummaryContext.state$,
    ]).subscribe(([phone, helpdeskOptions, siteSummary]) => {
      this._phone = phone;
      this._siteContext.setSiteForPublicPhone(siteSummary, this._phone);
      this._resetPinOnNextLogin = (helpdeskOptions as CiscoHelpdeskOptions).resetPinOnNextLoginEnabled;
      this.isLoading = false;
    });
    this._subscriptions.add(subscription);

    this.ziroFormsUpdate$.subscribe((data: ZiroFormsUpdate<PinReset>) => {
      if (data.old?.vmPinReset && !isEqual(data.new.vmPinReset, data.old.vmPinReset) && this._hasError) {
        this._hasError = false;
      }
    });
  }

  submit(): Observable<null> {
    return of(null);
  }

  onSubmit(): void {
    this._resetVoicemailPin();
  }

  private _getLineButton(): Button {
    if (this._phone.buttons.length > 0 && this._phone.buttons[0].type === 'Line') {
      return this._phone.buttons[0];
    }
  }

  private _resetVoicemailPin(): void {
    const lineButton = this._getLineButton();
    if (lineButton) {
      const serverId = (lineButton as LineButton).dn.serverId.toString();
      const dnId = (lineButton as LineButton).dn.id;
      this._dnDetailSummaryResource
        .get(dnId, serverId)
        .pipe(
          switchMap((dnDetailSummary) => {
            return this._pinResetService.resetVoicemailPin(
              dnDetailSummary.voicemail,
              this.entity.vmPinReset,
              this._resetPinOnNextLogin,
            );
          }),
        )
        .subscribe({
          next: () => {
            this._toastService.push(
              ToastTypes.SUCCESS,
              this._ziroIcons.REFRESH,
              'tkey;pages.pinreset.success.title',
              'tkey;pages.pinreset.voicemail.success.message',
            );
            this._resetVoicemailPinInput();
          },
          error: (error) => {
            if (error.status === 422) {
              this._hasError = true;
              this.errorMessage = this._pinResetService.translateVoicemailPinResetErrorResponse(error);
              this.fieldChannels['vmPinReset'].validateSource.next();
            } else {
              throw new HttpErrorResponse(error);
            }
            this._resetVoicemailPinInput();
          },
        });
    }
  }

  private _resetVoicemailPinInput(): void {
    this.vmPinResetComponent.isPending = false;
    this.entitySource.next({
      vmPinReset: '',
    });
    this._ziroFormStateService.setIsFormDirty(false);
  }
}
